import { reactive } from 'vue'
import store from '../store'

// Module registry
const modules = reactive(new Map())

// Event system
const eventListeners = reactive(new Map())

// Module states
const moduleStates = reactive(new Map())

class ModuleManager {
  constructor() {
    this.tourSettings = null
  }

  // Internal: sync a single module state to Vuex
  syncModuleState(moduleName) {
    const state = moduleStates.get(moduleName)
    try {
      store.dispatch('modules/setModuleState', {
        moduleName,
        isEnabled: !!state?.isEnabled,
        isLoaded: !!state?.isLoaded
      })
    } catch (e) {
      console.warn('[ModuleManager] Failed to sync module state to Vuex for', moduleName, e)
    }
  }

  // Internal: sync all module states to Vuex
  syncAllModuleStates() {
    try {
      // Only sync enabled/loaded flags to avoid overwriting persisted data
      moduleStates.forEach((st, name) => {
        store.dispatch('modules/setModuleState', {
          moduleName: name,
          isEnabled: !!st?.isEnabled,
          isLoaded: !!st?.isLoaded
        })
      })
    } catch (e) {
      console.warn('[ModuleManager] Failed to sync all module states to Vuex', e)
    }
  }

  // Register a module
  registerModule(moduleName, moduleConfig) {
    console.log(`[ModuleManager] Registering module: ${moduleName}`)
    console.log(`[ModuleManager] Module config:`, moduleConfig)
    
    const module = {
      name: moduleName,
      config: moduleConfig,
      isEnabled: false,
      isLoaded: false,
      ...moduleConfig
    }
    
    modules.set(moduleName, module)
    moduleStates.set(moduleName, {
      isEnabled: false,
      isLoaded: false,
      data: {}
    })

    // Seed in-memory data from persisted Vuex snapshot if available
    try {
      const persisted = store.getters['modules/getModuleState']?.(moduleName)?.data
      if (persisted && Object.keys(persisted).length > 0) {
        const st = moduleStates.get(moduleName)
        st.data = { ...persisted }
      }
    } catch (e) {
      // ignore
    }

    // Sync to Vuex
    this.syncModuleState(moduleName)
    
    console.log(`[ModuleManager] Module ${moduleName} registered successfully`)
    console.log(`[ModuleManager] Current registered modules:`, Array.from(modules.keys()))
    console.log(`[ModuleManager] Current module states:`, Array.from(moduleStates.keys()))
  }

  // Initialize modules based on tour settings
  initializeModules(tourSettings) {
    console.log('[ModuleManager] Initializing modules with tour settings:', tourSettings)
    console.log('[ModuleManager] Tour settings modules array:', tourSettings?.modules)
    this.tourSettings = tourSettings
    
    modules.forEach((module, moduleName) => {
      const isEnabled = this.isModuleEnabled(moduleName, tourSettings)
      const moduleState = moduleStates.get(moduleName)
      
      console.log(`[ModuleManager] Checking module ${moduleName}:`)
      console.log(`  - isEnabled: ${isEnabled}`)
      console.log(`  - current state:`, moduleState)
      console.log(`  - module object:`, module)
      
      if (isEnabled && !moduleState.isEnabled) {
        console.log(`[ModuleManager] Enabling module: ${moduleName}`)
        moduleState.isEnabled = true
        
        // Call module's onEnable method if it exists
        const moduleInstance = module.config
        if (moduleInstance && moduleInstance.onEnable) {
          try {
            console.log(`[ModuleManager] Calling onEnable for module: ${moduleName}`)
            moduleInstance.onEnable(tourSettings)
            moduleState.isLoaded = true
            console.log(`[ModuleManager] Module ${moduleName} enabled and loaded`)
            this.syncModuleState(moduleName)
          } catch (error) {
            console.error(`[ModuleManager] Error enabling module ${moduleName}:`, error)
          }
        } else {
          // If no onEnable method, just mark as loaded
          moduleState.isLoaded = true
          console.log(`[ModuleManager] Module ${moduleName} enabled (no onEnable method)`)
          this.syncModuleState(moduleName)
        }
      } else if (!isEnabled && moduleState.isEnabled) {
        console.log(`[ModuleManager] Disabling module: ${moduleName}`)
        moduleState.isEnabled = false
        moduleState.isLoaded = false
        this.syncModuleState(moduleName)
        
        // Call module's onDisable method if it exists
        const moduleInstance = module.config
        if (moduleInstance && moduleInstance.onDisable) {
          try {
            moduleInstance.onDisable()
            console.log(`[ModuleManager] Module ${moduleName} disabled`)
          } catch (error) {
            console.error(`[ModuleManager] Error disabling module ${moduleName}:`, error)
          }
        }
      } else {
        console.log(`[ModuleManager] Module ${moduleName} state unchanged (enabled: ${isEnabled}, wasEnabled: ${moduleState.isEnabled})`)
      }
    })
    
    console.log('[ModuleManager] Final module states:', Array.from(moduleStates.entries()))
    // Sync all to Vuex after initialization pass
    this.syncAllModuleStates()
  }

  // Check if a module is enabled based on tour settings
  isModuleEnabled(moduleName, tourSettings) {
    console.log(`[ModuleManager] Checking if module '${moduleName}' is enabled:`)
    console.log(`  - tourSettings:`, tourSettings)
    console.log(`  - tourSettings.modules:`, tourSettings?.modules)
    
    if (!tourSettings) {
      console.log(`  - Result: false (no tourSettings)`)
      return false
    }
    
    // Check if module is in the modules array
    if (tourSettings.modules && Array.isArray(tourSettings.modules)) {
      const isInArray = tourSettings.modules.includes(moduleName)
      console.log(`  - Found modules array:`, tourSettings.modules)
      console.log(`  - Module '${moduleName}' in array: ${isInArray}`)
      console.log(`  - Result: ${isInArray}`)
      return isInArray
    }
    
    console.log(`  - No modules array found, using fallback logic`)
    
    // Fallback to the old nested structure approach for backward compatibility
    switch (moduleName) {
      case 'routing':
        const routingEnabled = !!(tourSettings.navigation?.routing?.active)
        console.log(`  - Routing fallback check: ${routingEnabled}`)
        return routingEnabled
      case 'game':
        const gameEnabled = !!(tourSettings.game?.active)
        console.log(`  - Game fallback check: ${gameEnabled}`)
        console.log(`  - tourSettings.game:`, tourSettings.game)
        return gameEnabled
      case 'qr_code':
        const qrEnabled = !!(tourSettings.navigation?.qr_code?.active)
        console.log(`  - QR code fallback check: ${qrEnabled}`)
        return qrEnabled
      case 'outdoor_map':
        const outdoorEnabled = !!(tourSettings.navigation?.gps?.active)
        console.log(`  - Outdoor map fallback check: ${outdoorEnabled}`)
        return outdoorEnabled
      default:
        // Check if there's a direct module setting
        const directEnabled = !!(tourSettings[moduleName]?.active)
        console.log(`  - Direct module check for '${moduleName}': ${directEnabled}`)
        return directEnabled
    }
  }

  // Get module state
  getModuleState(moduleName) {
    return moduleStates.get(moduleName) || { isEnabled: false, isLoaded: false, data: {} }
  }

  // Check if module is enabled
  isModuleActive(moduleName) {
    const state = this.getModuleState(moduleName)
    return state.isEnabled && state.isLoaded
  }

  // Get all active modules
  getActiveModules() {
    const activeModules = []
    modules.forEach((module, moduleName) => {
      if (this.isModuleActive(moduleName)) {
        activeModules.push(moduleName)
      }
    })
    return activeModules
  }

  // Event system
  addEventListener(eventName, callback, moduleName = null) {
    if (!eventListeners.has(eventName)) {
      eventListeners.set(eventName, [])
    }
    
    eventListeners.get(eventName).push({
      callback,
      moduleName
    })
    
    console.log(`[ModuleManager] Event listener added for ${eventName}${moduleName ? ` (${moduleName})` : ''}`)
  }

  removeEventListener(eventName, callback, moduleName = null) {
    if (!eventListeners.has(eventName)) return
    
    const listeners = eventListeners.get(eventName)
    const index = listeners.findIndex(listener => 
      listener.callback === callback && listener.moduleName === moduleName
    )
    
    if (index !== -1) {
      listeners.splice(index, 1)
      console.log(`[ModuleManager] Event listener removed for ${eventName}`)
    }
  }

  // Dispatch event to all listeners
  dispatchEvent(eventName, data = {}) {
    console.log(`[ModuleManager] Dispatching event: ${eventName}`, data)
    
    if (!eventListeners.has(eventName)) {
      console.log(`[ModuleManager] No listeners found for event: ${eventName}`)
      return
    }
    
    const listeners = eventListeners.get(eventName)
    console.log(`[ModuleManager] Found ${listeners.length} listeners for event: ${eventName}`)
    console.log(`[ModuleManager] Listeners for ${eventName}:`, listeners)
    
    listeners.forEach(({ callback, moduleName }, index) => {
      console.log(`[ModuleManager] Calling listener ${index + 1} for module: ${moduleName}`)
      console.log(`[ModuleManager] Module ${moduleName} active: ${this.isModuleActive(moduleName)}`)
      try {
        // Only call callback if module is active (if moduleName is specified)
        if (!moduleName || this.isModuleActive(moduleName)) {
          console.log(`[ModuleManager] Executing callback for module: ${moduleName}`)
          const result = callback(data)
          console.log(`[ModuleManager] Listener ${index + 1} returned:`, result)
        } else {
          console.log(`[ModuleManager] Skipping listener for inactive module: ${moduleName}`)
        }
      } catch (error) {
        console.error(`[ModuleManager] Error in event listener for ${eventName}${moduleName ? ` (${moduleName})` : ''}:`, error)
      }
    })
  }

  // Module-specific data storage
  setModuleData(moduleName, data) {
    const state = moduleStates.get(moduleName)
    if (state) {
      state.data = { ...state.data, ...data }
      try {
        store.dispatch('modules/setModuleData', { moduleName, data })
      } catch (e) {
        console.warn('[ModuleManager] Failed to sync module data to Vuex for', moduleName, e)
      }
    }
  }

  getModuleData(moduleName) {
    const state = moduleStates.get(moduleName)
    return state ? state.data : {}
  }

  // Debug method to show current state
  debugState() {
    console.log('=== MODULE MANAGER DEBUG STATE ===')
    console.log('Registered modules:', Array.from(modules.keys()))
    console.log('Module states:', Array.from(moduleStates.entries()))
    console.log('Tour settings:', this.tourSettings)
    console.log('Tour settings modules array:', this.tourSettings?.modules)
    console.log('Event listeners:', Array.from(eventListeners.entries()))
    
    modules.forEach((module, moduleName) => {
      const state = moduleStates.get(moduleName)
      const isEnabled = this.isModuleEnabled(moduleName, this.tourSettings)
      const moduleInstance = this.getModule(moduleName)
      
      console.log(`Module ${moduleName}:`)
      console.log(`  - Registered: ${!!module}`)
      console.log(`  - State enabled: ${state?.isEnabled}`)
      console.log(`  - State loaded: ${state?.isLoaded}`)
      console.log(`  - Should be enabled: ${isEnabled}`)
      console.log(`  - Is active: ${this.isModuleActive(moduleName)}`)
      console.log(`  - Is ready: ${this.isModuleReady(moduleName)}`)
      console.log(`  - Module instance:`, moduleInstance)
      
      // Check for common methods if module instance exists
      if (moduleInstance) {
        const commonMethods = ['onEnable', 'onDisable', 'processAllPoints', 'shouldShowPoint', 'shouldUnlockPoint']
        console.log(`  - Available methods:`)
        commonMethods.forEach(method => {
          console.log(`    - ${method}: ${typeof moduleInstance[method]}`)
        })
      }
    })
    console.log('=== END DEBUG STATE ===')
  }

  // Get event listeners for debugging
  getEventListeners() {
    return eventListeners
  }

  // Get module instance
  getModule(moduleName) {
    const module = modules.get(moduleName)
    if (!module) {
      console.log(`[ModuleManager] Module ${moduleName} not found`)
      return null
    }
    
    // Return the actual module instance (config) which contains all the methods
    console.log(`[ModuleManager] Returning module instance for ${moduleName}:`, module.config)
    return module.config
  }

  // Check if a module is ready (active and has required methods)
  isModuleReady(moduleName, requiredMethods = []) {
    if (!this.isModuleActive(moduleName)) {
      console.log(`[ModuleManager] Module ${moduleName} is not active`)
      return false
    }
    
    const module = this.getModule(moduleName)
    if (!module) {
      console.log(`[ModuleManager] Module ${moduleName} not found`)
      return false
    }
    
    // Check if all required methods exist
    for (const method of requiredMethods) {
      if (typeof module[method] !== 'function') {
        console.log(`[ModuleManager] Module ${moduleName} missing required method: ${method}`)
        return false
      }
    }
    
    console.log(`[ModuleManager] Module ${moduleName} is ready with all required methods:`, requiredMethods)
    return true
  }

  // Force re-enable a module (useful for debugging)
  forceReEnableModule(moduleName) {
    console.log(`[ModuleManager] Force re-enabling module: ${moduleName}`)
    
    const module = modules.get(moduleName)
    if (!module) {
      console.log(`[ModuleManager] Module ${moduleName} not found`)
      return false
    }
    
    const moduleState = moduleStates.get(moduleName)
    if (!moduleState) {
      console.log(`[ModuleManager] Module state for ${moduleName} not found`)
      return false
    }
    
    // Reset state
    moduleState.isEnabled = false
    moduleState.isLoaded = false
    
    // Re-enable if it should be enabled
    if (this.isModuleEnabled(moduleName, this.tourSettings)) {
      console.log(`[ModuleManager] Re-enabling module: ${moduleName}`)
      moduleState.isEnabled = true
      
      const moduleInstance = module.config
      if (moduleInstance && moduleInstance.onEnable) {
        try {
          moduleInstance.onEnable(this.tourSettings)
          moduleState.isLoaded = true
          console.log(`[ModuleManager] Module ${moduleName} re-enabled successfully`)
          this.syncModuleState(moduleName)
          return true
        } catch (error) {
          console.error(`[ModuleManager] Error re-enabling module ${moduleName}:`, error)
          return false
        }
      } else {
        moduleState.isLoaded = true
        console.log(`[ModuleManager] Module ${moduleName} re-enabled (no onEnable method)`)
        this.syncModuleState(moduleName)
        return true
      }
    }
    
    return false
  }
}

// Create singleton instance
const moduleManager = new ModuleManager()

export default moduleManager 